import { useState } from 'react';

import {
  PrimaryButton,
  SecondaryButton,
} from '@changegps/horizon-component-library';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  useSaveUserProfile,
  useStoreImage,
  useUserProfileFormContext,
} from '../hooks';
import { hasUserProfileChanged } from '../store';
import { UserProfile } from '../types';

interface ActionButtonGroupsProps {
  position?: 'top' | 'bottom';
}

export const ActionButtonGroups = ({ position }: ActionButtonGroupsProps) => {
  const form = useUserProfileFormContext();
  const { mutate: saveProfile } = useSaveUserProfile();
  const { mutate: storeImage } = useStoreImage();

  const [isSaving, setIsSaving] = useState(false);

  const watchedValues = useWatch({ control: form.control });

  const { profile_pic, signature_pic, ...profileValues } = watchedValues;

  const hasProfileChanged = hasUserProfileChanged(profileValues as UserProfile);
  const hasNewImages =
    profile_pic instanceof File || signature_pic instanceof File;

  const handleSaveProfile = form.handleSubmit(async (values) => {
    try {
      setIsSaving(true); // Start loading state

      const formData = new FormData();
      const updatedValues = { ...values };

      // Append image files if they exist
      if (values.profile_pic instanceof File) {
        formData.append('profile_pic', values.profile_pic);
        delete updatedValues.profile_pic;
      }

      if (values.signature_pic instanceof File) {
        formData.append('signature_pic', values.signature_pic);
        delete updatedValues.signature_pic;
      }

      // Function to upload images
      const saveImageAsync = (formData: FormData) =>
        new Promise((resolve, reject) => {
          storeImage(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });

      // Upload images if necessary
      if (formData.has('profile_pic') || formData.has('signature_pic')) {
        if (hasNewImages) {
          await saveImageAsync(formData);

          // Reset file inputs manually
          form.setValue('profile_pic', '');
          form.setValue('signature_pic', '');
        }
      }

      // Function to save profile
      const saveProfileAsync = (updatedValues: UserProfile) =>
        new Promise((resolve, reject) => {
          saveProfile(updatedValues, {
            onSuccess: resolve,
            onError: reject,
          });
        });

      // Save profile
      if (hasProfileChanged) {
        await saveProfileAsync(updatedValues);
      }
      // Show success toast if everything succeeded
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Error saving profile');
    } finally {
      setIsSaving(false); // Reset loading state
    }
  });

  return (
    <Box
      display="flex"
      justifyContent={position === 'top' ? 'space-between' : 'flex-end'}
    >
      {position === 'top' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="500">
            Edit Profile Information
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <SecondaryButton>Cancel</SecondaryButton>
        <PrimaryButton
          disabled={(!hasProfileChanged && !hasNewImages) || isSaving}
          onClick={handleSaveProfile}
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};
